import React, { ChangeEvent, useEffect, useState, useId } from "react";

import { OrderProps } from "../../types/ComponentProps";

import { Modal } from "../ui/modal/Modal";

import Input from "../input/Input";
import Spinner from "../spinner/Spinner";
import CustomSelect from "../customSelect/CustomSelect";
import { provinceOptions } from "../editProfile/dropdown";

import ProductDetailsModalStyles from "./ProductDetailsModalStyles.module.css";

type OrderDetailAddressProps = {
  order: OrderProps;
  setOrderForm: React.Dispatch<React.SetStateAction<OrderProps>>;
  confirmOrderDetails: () => void;
  setLoadingOrder: React.Dispatch<
    React.SetStateAction<{ message: string; open: boolean }>
  >;
  loadingOrder: {
    message: string;
    open: boolean;
  };
  cooldownLeft: number;
  onClose: () => void;
};

const OrderDetailAddress = ({
  order,
  setOrderForm,
  loadingOrder,
  setLoadingOrder,
  confirmOrderDetails,
  cooldownLeft,
  onClose,
}: OrderDetailAddressProps) => {
  const [errors, setErrors] = useState(false);
  const provinceId = useId();
  const landmarksId = useId();
  const errorId = useId();

  useEffect(() => {
    setLoadingOrder((prev) => ({ ...prev, message: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeHandler = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (errors) {
      setErrors(false);
    }
    setOrderForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSelect =
    (field: "province") =>
    (selectedOption: { value: string; label: string }) => {
      if (errors) {
        setErrors(false);
      }
      setOrderForm((previous) => ({
        ...previous,
        [field]: selectedOption.value,
      }));
    };

  const handleSubmit = () => {
    const hasError =
      !order.mobilePhone ||
      order.mobilePhone.length !== 11 ||
      !order.city ||
      !order.province ||
      !order.postalCode ||
      !order.landmarks;

    if (hasError) {
      setErrors(true);
      return;
    }

    confirmOrderDetails();
  };

  return (
    <Modal title="Confirm Delivery Address" open={true} onClose={onClose}>
      <div 
        className={ProductDetailsModalStyles.productFormSection}
        role="form"
        aria-label="Delivery address form"
      >
        <div>
          <Input
            type="text"
            name="fullName"
            value={order?.fullName}
            onChangeHandler={onChangeHandler}
            label="Name"
            disabled={true}
          />
          <div className={ProductDetailsModalStyles.flexBoxBlk}>
            <div>
              <Input
                type="text"
                value={order?.email}
                name="email"
                onChangeHandler={onChangeHandler}
                label="Email"
                disabled={true}
              />
            </div>
            <div>
              <Input
                type="number"
                value={order?.mobilePhone}
                name="mobilePhone"
                onChangeHandler={onChangeHandler}
                label="Mobile Number"
                aria-describedby="mobile-hint"
                aria-invalid={errors && (!order.mobilePhone || order.mobilePhone.length !== 11)}
              />
              <p id="mobile-hint" className={ProductDetailsModalStyles.inputHint}>
                Must be exactly 11 digits
              </p>
            </div>
          </div>
          <div
            style={{ marginBottom: "20px" }}
            className={ProductDetailsModalStyles.flexBoxBlk}
          >
            <div>
              <Input
                type="text"
                value={order?.city}
                name="city"
                onChangeHandler={onChangeHandler}
                label="City"
                aria-invalid={errors && !order.city}
              />
            </div>
            <div>
              <label id={provinceId}>Province</label>
              <CustomSelect
                options={provinceOptions}
                value={order?.province}
                onSelect={handleSelect("province")}
                placeholder={"Select a Province"}
              />
            </div>
            <div>
              <Input
                type="number"
                value={order?.postalCode}
                name="postalCode"
                onChangeHandler={onChangeHandler}
                label="Postal Code"
                aria-invalid={errors && !order.postalCode}
              />
            </div>
          </div>
          <div>
            <label htmlFor={landmarksId}>Landmarks</label>
            <textarea
              id={landmarksId}
              spellCheck="false"
              maxLength={160}
              name="landmarks"
              value={order?.landmarks}
              onChange={onChangeHandler}
              aria-describedby="landmarks-hint"
              aria-invalid={errors && !order.landmarks}
            ></textarea>
            <div className={ProductDetailsModalStyles.footnote}>
              <p id="landmarks-hint">Limited to 160 characters</p>
            </div>
          </div>

          <hr />

          {/* Footer Section */}
          <div
            style={{
              display: "flex",
              margin: "10px",
              justifyContent: "flex-end",
            }}
            role="alert"
            aria-live="polite"
          >
            {errors ? (
              <p id={errorId} className={ProductDetailsModalStyles.errorText}>
                All fields are required
              </p>
            ) : (
              loadingOrder.message && <p>{loadingOrder.message}</p>
            )}
          </div>
          <div className={ProductDetailsModalStyles.orderButtonSectionConfirm}>
            <button
              type="button"
              disabled={loadingOrder.open || cooldownLeft > 0}
              onClick={handleSubmit}
              style={{
                opacity: cooldownLeft > 0 ? "50%" : "",
                cursor: cooldownLeft > 0 ? "default" : "",
              }}
              className={ProductDetailsModalStyles.orderDetailsConfirmButton}
              aria-describedby={errors ? errorId : undefined}
            >
              {loadingOrder.open ? (
                <Spinner variant="small-spinner" alignSpin="center-spinner" />
              ) : cooldownLeft > 0 ? (
                `Please wait ${Math.ceil(cooldownLeft / 1000)}s`
              ) : (
                <>
                  Confirm Order <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OrderDetailAddress;
